import React, { useMemo } from "react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";

import { GOOGLE_MAPS_LOADER_OPTIONS } from "./googleMaps";

const containerStyle = { width: "100%", height: "100%" };

// Identidad estable a proposito: GoogleMap corre map.setOptions(options) en un useEffect con
// dependencia [map, options], asi que un objeto inline se reaplicaria en cada render.
const mapOptions = {
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: false,
};

const mensajeStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: "#5f6b7a",
    fontSize: "13px",
};

const toNumber = (value) =>
    typeof value === "number" ? value : parseFloat(value);

/**
 * Mapa con pin arrastrable.
 *
 * El pin y el centro de la vista van por separado a proposito:
 *
 *   - `centro` es a donde mira el mapa. Solo cambia cuando la ubicacion la resolvio la
 *     direccion del formulario: cambio la direccion, hay que ir a verla.
 *   - `lat` / `lng` son la posicion del pin. Al arrastrarlo se mueve solo el pin.
 *
 * Si el centro siguiera al pin, arrastrarlo al borde haria saltar la vista para centrarlo, y
 * el usuario perderia la referencia de donde lo estaba dejando.
 *
 * @param onArrastrarPin recibe { lat, lng }. Si no se pasa, el pin queda fijo y el mapa es de
 *                       solo lectura (se puede navegar igual).
 */
export default function MapaPreview({
    lat,
    lng,
    centro,
    zoom = 15,
    onArrastrarPin,
}) {
    const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

    const posicionPin = useMemo(
        () => ({ lat: toNumber(lat), lng: toNumber(lng) }),
        [lat, lng]
    );

    // GoogleMap hace map.setCenter(center) en un useEffect con dependencia [map, center], que
    // compara por identidad. Con un objeto literal se recentraria en cada render y el usuario
    // no podria panear. Memoizado, solo se recentra cuando cambia el centro de verdad.
    //
    // Sin `centro` cae en la posicion del pin, que es el comportamiento de un mapa de lectura.
    //
    // OJO con las dependencias: lat y lng son el fallback, pero tenerlas en la lista hacia que
    // al soltar el pin el memo devolviera un objeto NUEVO con los mismos valores de centro.
    // Identidad nueva = setCenter, asi que el mapa volvia al centro de la localidad y el pin
    // se perdia de vista justo despues de haberlo acomodado. Por eso se resuelven los valores
    // primero y el memo depende de esos: cuando hay `centro`, mover el pin no lo cambia.
    const centroLat = toNumber(centro?.lat ?? lat);
    const centroLng = toNumber(centro?.lng ?? lng);
    const centroMapa = useMemo(
        () => ({ lat: centroLat, lng: centroLng }),
        [centroLat, centroLng]
    );

    if (!isLoaded) {
        return <div style={mensajeStyle}>Cargando mapa...</div>;
    }

    if (Number.isNaN(posicionPin.lat) || Number.isNaN(posicionPin.lng)) {
        return <div style={mensajeStyle}>Sin coordenadas</div>;
    }

    return (
        <GoogleMap
            mapContainerStyle={containerStyle}
            center={centroMapa}
            zoom={zoom}
            options={mapOptions}
        >
            <Marker
                position={posicionPin}
                draggable={Boolean(onArrastrarPin)}
                // onDragEnd y no onDrag: reportar en cada frame del arrastre dispararia un
                // render por movimiento del mouse.
                onDragEnd={
                    onArrastrarPin
                        ? (evento) =>
                              onArrastrarPin({
                                  lat: evento.latLng.lat(),
                                  lng: evento.latLng.lng(),
                              })
                        : undefined
                }
            />
        </GoogleMap>
    );
}
